import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Repeat, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  createExpense,
  createRentPayment,
  removeExpense,
  removeRentPayment,
  updateExpense,
  updateRentPayment,
} from "@/services/dataService";
import { EXPENSE_CATEGORY_OPTIONS, LEDGER_CATEGORIES, categoryLabel } from "@/lib/categories";
import { dayToTimestamp, formatMoney, houseName, ledgerTotals, timestampToDay } from "@/lib/ledger";

const todayDay = () => timestampToDay(Date.now());
const BUSINESS = "";

function parseAmount(value) {
  const number = Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(number) ? Math.abs(number) : null;
}

/** Click to edit; Enter or leaving the field saves, Escape cancels. */
function EditableCell({ value, display, type = "text", options, onSave, align, label }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      if (type !== "select" && type !== "date") ref.current?.select?.();
    }
  }, [editing, type]);

  async function commit() {
    setEditing(false);
    if (draft === value) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={`hh-cell-button ${align === "right" ? "text-right tabular-nums" : ""}`}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        aria-label={`Edit ${label}`}
      >
        {saving ? <Loader2 className="inline h-3 w-3 animate-spin" /> : display ?? value}
      </button>
    );
  }

  const common = {
    ref,
    "aria-label": label,
    className: `hh-cell-input ${align === "right" ? "text-right" : ""}`,
    onBlur: commit,
    onKeyDown: (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commit();
      } else if (event.key === "Escape") {
        setDraft(value);
        setEditing(false);
      }
    },
  };
  if (type === "select") {
    return (
      <select {...common} value={draft} onChange={(event) => setDraft(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      {...common}
      type={type === "date" ? "date" : "text"}
      inputMode={type === "money" ? "decimal" : undefined}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
    />
  );
}

/**
 * The spreadsheet-like ledger: an always-open entry row on top, click any
 * cell to edit, totals at the bottom. `lockedHouse` pins it to one house.
 */
export default function LedgerTable({ rows, properties, tenants, lockedHouse, onChanged, emptyMessage }) {
  const { toast } = useToast();
  const propertiesById = useMemo(() => new Map(properties.map((p) => [String(p._id), p])), [properties]);
  const houseOptions = useMemo(
    () => [{ value: BUSINESS, label: "Business (no house)" }, ...properties.map((p) => ({ value: String(p._id), label: houseName(p) }))],
    [properties],
  );
  const [draft, setDraft] = useState({
    day: todayDay(),
    house: lockedHouse || BUSINESS,
    description: "",
    category: "other",
    amount: "",
  });
  const [adding, setAdding] = useState(false);
  const descriptionRef = useRef(null);
  const totals = ledgerTotals(rows);

  useEffect(() => {
    if (lockedHouse) setDraft((prev) => ({ ...prev, house: lockedHouse }));
  }, [lockedHouse]);

  const fail = (error) => toast({ title: "Couldn't save", description: error.message, variant: "destructive" });

  async function addRow() {
    const amount = parseAmount(draft.amount);
    const date = dayToTimestamp(draft.day);
    if (!draft.description.trim() || !amount || !date) {
      toast({ title: "Add a date, a description and an amount", variant: "destructive" });
      return;
    }
    if (draft.category === "rent" && !draft.house) {
      toast({ title: "Rent needs a house", variant: "destructive" });
      return;
    }
    setAdding(true);
    try {
      if (draft.category === "rent") {
        const tenant = tenants.find((t) => String(t.propertyId) === draft.house && t.isActive);
        await createRentPayment({
          propertyId: draft.house,
          tenantId: tenant?._id,
          amount,
          paymentDate: date,
          status: "completed",
          notes: draft.description.trim(),
        });
      } else {
        await createExpense({
          propertyId: draft.house || undefined,
          title: draft.description.trim(),
          amount,
          category: draft.category,
          date,
          isRecurring: false,
        });
      }
      // Keep date, house and category for the next line — fast monthly entry.
      setDraft((prev) => ({ ...prev, description: "", amount: "" }));
      descriptionRef.current?.focus();
      onChanged?.();
    } catch (error) {
      fail(error);
    } finally {
      setAdding(false);
    }
  }

  async function saveField(row, field, value) {
    try {
      if (row.kind === "rent") {
        const patch = { id: row.id };
        if (field === "day") patch.paymentDate = dayToTimestamp(value);
        if (field === "amount") patch.amount = parseAmount(value);
        if (field === "description") patch.notes = value;
        if (field === "house") patch.propertyId = value;
        if (Object.values(patch).some((v) => v === null)) throw new Error("That value doesn't look right.");
        await updateRentPayment(patch);
      } else {
        const patch = { id: row.id };
        if (field === "day") patch.date = dayToTimestamp(value);
        if (field === "amount") patch.amount = parseAmount(value);
        if (field === "description") patch.title = value.trim();
        if (field === "category") patch.category = value;
        if (field === "house") {
          if (value) patch.propertyId = value;
          else patch.clearPropertyId = true;
        }
        if (Object.values(patch).some((v) => v === null || v === "")) throw new Error("That value doesn't look right.");
        await updateExpense(patch);
      }
      onChanged?.();
    } catch (error) {
      fail(error);
    }
  }

  async function deleteRow(row) {
    if (!window.confirm(`Delete “${row.description}” (${formatMoney(row.amount)})?`)) return;
    try {
      if (row.kind === "rent") await removeRentPayment(row.id);
      else await removeExpense(row.id);
      onChanged?.();
      toast({ title: "Deleted" });
    } catch (error) {
      fail(error);
    }
  }

  const onDraftKey = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addRow();
    }
  };
  const set = (key) => (event) => setDraft((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <div className="hh-panel overflow-hidden">
      <div className="max-h-[70vh] overflow-auto">
        <table className="hh-table min-w-[760px]">
          <thead>
            <tr>
              <th className="w-[132px]">Date</th>
              {!lockedHouse ? <th className="w-[180px]">House</th> : null}
              <th>Description</th>
              <th className="w-[170px]">Category</th>
              <th className="num w-[120px]">Amount</th>
              <th className="w-[44px]">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-accent/40">
              <td>
                <input className="hh-cell-input" type="date" value={draft.day} onChange={set("day")} onKeyDown={onDraftKey} aria-label="New row date" />
              </td>
              {!lockedHouse ? (
                <td>
                  <select className="hh-cell-input" value={draft.house} onChange={set("house")} onKeyDown={onDraftKey} aria-label="New row house">
                    {houseOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
              ) : null}
              <td>
                <input
                  ref={descriptionRef}
                  className="hh-cell-input"
                  value={draft.description}
                  onChange={set("description")}
                  onKeyDown={onDraftKey}
                  placeholder="New entry — e.g. PennyMac mortgage"
                  aria-label="New row description"
                />
              </td>
              <td>
                <select className="hh-cell-input" value={draft.category} onChange={set("category")} onKeyDown={onDraftKey} aria-label="New row category">
                  {LEDGER_CATEGORIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.income ? `${option.label} (money in)` : option.label}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  className="hh-cell-input text-right"
                  inputMode="decimal"
                  value={draft.amount}
                  onChange={set("amount")}
                  onKeyDown={onDraftKey}
                  placeholder="0.00"
                  aria-label="New row amount"
                />
              </td>
              <td>
                <button
                  type="button"
                  onClick={addRow}
                  disabled={adding}
                  className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground disabled:opacity-50"
                  aria-label="Add row"
                  title="Add (Enter)"
                >
                  {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </button>
              </td>
            </tr>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={lockedHouse ? 5 : 6} className="py-10 text-center text-sm text-muted-foreground">
                  {emptyMessage || "Nothing here yet. Type your first entry in the row above."}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const property = propertiesById.get(row.propertyId);
              const categoryOptions = row.kind === "rent" ? null : EXPENSE_CATEGORY_OPTIONS.some((o) => o.value === row.category)
                ? EXPENSE_CATEGORY_OPTIONS
                : [...EXPENSE_CATEGORY_OPTIONS, { value: row.category, label: categoryLabel(row.category) }];
              return (
                <tr key={`${row.kind}-${row.id}`} className="group">
                  <td className="whitespace-nowrap">
                    <EditableCell
                      label="date"
                      type="date"
                      value={row.day}
                      display={row.day ? new Date(dayToTimestamp(row.day)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      onSave={(value) => saveField(row, "day", value)}
                    />
                  </td>
                  {!lockedHouse ? (
                    <td>
                      <EditableCell
                        label="house"
                        type="select"
                        options={row.kind === "rent" ? houseOptions.slice(1) : houseOptions}
                        value={row.propertyId}
                        display={property ? houseName(property) : <span className="text-muted-foreground">Business</span>}
                        onSave={(value) => saveField(row, "house", value)}
                      />
                    </td>
                  ) : null}
                  <td>
                    <span className="flex items-center gap-1.5">
                      <EditableCell
                        label="description"
                        value={row.description}
                        display={row.description || <span className="text-muted-foreground">—</span>}
                        onSave={(value) => saveField(row, "description", value)}
                      />
                      {row.recurring ? <Repeat className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Repeats" /> : null}
                      {row.pending ? <span className="shrink-0 rounded bg-amber-500/15 px-1.5 text-[11px] text-amber-800 dark:text-amber-300">due</span> : null}
                    </span>
                    {row.notes ? <span className="block truncate text-xs text-muted-foreground">{row.notes}</span> : null}
                  </td>
                  <td>
                    {categoryOptions ? (
                      <EditableCell
                        label="category"
                        type="select"
                        options={categoryOptions}
                        value={row.category}
                        display={categoryLabel(row.category)}
                        onSave={(value) => saveField(row, "category", value)}
                      />
                    ) : (
                      <span className="px-0.5">Rent</span>
                    )}
                  </td>
                  <td className={`num ${row.amount > 0 ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                    <EditableCell
                      label="amount"
                      type="money"
                      align="right"
                      value={Math.abs(row.amount).toFixed(2)}
                      display={formatMoney(row.amount, { sign: true })}
                      onSave={(value) => saveField(row, "amount", value)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => deleteRow(row)}
                      className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100"
                      aria-label={`Delete ${row.description}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={lockedHouse ? 2 : 3} className="text-muted-foreground">
                {totals.count} {totals.count === 1 ? "entry" : "entries"}
              </td>
              <td colSpan={3} className="num">
                <span className="text-muted-foreground">In </span>
                <span className="text-emerald-700 dark:text-emerald-300">{formatMoney(totals.moneyIn, { sign: true })}</span>
                <span className="mx-3 text-muted-foreground">Out −{formatMoney(totals.moneyOut)}</span>
                <span className={totals.net < 0 ? "text-rose-700 dark:text-rose-300" : ""}>Net {formatMoney(totals.net, { sign: true })}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
